'use client';

import clsx from 'clsx';
import { Heading2 } from '@/components/section/headings';
import type { HomeTestimonial } from '@/constants/homeMarketing';
import { Blockquote, P, Span, Strong, surfaceCardInteractive } from '@/components/ui';

export type HomeTestimonialsSectionProps = {
  title: string;
  subtitle?: string;
  items: readonly HomeTestimonial[];
  sectionId?: string;
  className?: string;
};

function RatingStars({ rating = 5 }: { rating?: number }) {
  return (
    <div className="flex gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Span
          key={i}
          className={clsx('text-sm', i < rating ? 'text-[var(--color-warning)]' : 'text-prose-muted/40')}
          aria-hidden
        >
          ★
        </Span>
      ))}
    </div>
  );
}

/** Client quotes in a responsive glass grid — matches home `text-prose*` rhythm. */
const HomeTestimonialsSection: React.FC<HomeTestimonialsSectionProps> = ({
  title,
  subtitle,
  items,
  sectionId,
  className,
}) => (
  <section
    id={sectionId}
    className={clsx('container-section', className)}
    aria-labelledby="home-testimonials-heading"
  >
    <div className="page-heading-wrap" id="home-testimonials-heading">
      <Heading2
        title={title}
        subtitle={subtitle}
        titleClassName="text-prose"
        subtitleClassName="mt-3 text-prose-muted"
      />
    </div>
    <div className="page-content-grid page-grid-3">
      {items.map((t) => (
        <figure
          key={t.id}
          className={surfaceCardInteractive('m-0 flex flex-col')}
        >
          <RatingStars rating={t.rating} />
          <Blockquote className="m-0 mt-4 flex-1 border-0 p-0 text-sm leading-relaxed text-prose">
            <P className="m-0 italic">&ldquo;{t.quote}&rdquo;</P>
          </Blockquote>
          <figcaption className="mt-5 border-t border-[color-mix(in_srgb,var(--semantic-border-default)_30%,transparent)] pt-4">
            <Strong className="text-sm text-prose">{t.name}</Strong>
            <P className="m-0 mt-1 text-xs text-prose-muted">
              {t.role}
              {t.company ? ` · ${t.company}` : ''}
            </P>
          </figcaption>
        </figure>
      ))}
    </div>
  </section>
);

export default HomeTestimonialsSection;
